import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalHeader } from '@creditornot/cb-modal';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import { orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { getAdCampaigns } from 'apis/ad-bookings/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { reportEvent } from 'modules/campaigns/behavioral';

import { CampaignsQueryKeys } from '../../campaignsQueryKeys';
import CampaignError from '../../components/CampaignError';
import CampaignHeader from '../../components/CampaignHeader';
import CampaignLimitAlert from '../../components/CampaignLimitAlert';
import CampaignTable from '../../components/CampaignTable/CampaignTable';
import { CampaignColumnIds } from '../../components/CampaignTable/types';
import CampaignTypeSelector from '../../components/CampaignTypeSelector/CampaignTypeSelector';
import { CampaignListMetrics } from '../../components/analytics/CampaignListMetrics';
import { transformPeriodToDateRange } from '../../components/analytics/FilterDateRangePicker';
import { useCampaignModuleContext } from '../../context';
import { useActiveCampaignsCount } from '../../hooks/useActiveCampaignsCount';
import { useCampaignConfiguration } from '../../hooks/useCampaignConfiguration';
import { useCampaignHandlers } from '../../hooks/useCampaignHandlers';
import { useCampaignsLimitStatus } from '../../hooks/useCampaignsLimitStatus';
import { useCampaignsList } from '../../hooks/useCampaignsList';
import { useCampaignsPerformance } from '../../hooks/useCampaignsPerformance';
import { useCampaignsRoutes } from '../../hooks/useCampaignsRoutes';
import { useIsCampaignAnalyticsForListPageEnabled } from '../../hooks/useIsCampaignAnalyticsForListPageEnabled';
import { CampaignListItem, DateRange } from '../../types';
import {
  telemetryInteractionClickCampaignTable,
  telemetryInteractionClickNewCampaign,
  useTelemetryCampaignsView,
} from '../../utils/telemetry/telemetry';
import { transformApiAdCampaignListItemToCampaignListItem } from '../../utils/transformApiCampaignToCampaignListItem/transformApiAdCampaignListItemToCampaignListItem';

export type ListCampaignsPageProps = {
  merchantId: string;
  venueIds?: string[];
  campaignTypesPermittedToEdit?: CampaignType[];
  timezone: string;
  country: Country;
  merchantVenues?: Venue[];
};

const StyledWoltModal = styled(WoltModal)<{ isMobile: boolean }>`
  width: 640px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: ${({ isMobile }) => (isMobile ? '0' : '8px')};
`;

const NewCampaignContainer = styled.div`
  display: flex;
  justify-content: center;
  max-width: 1208px;
  width: 100%;
  margin: 0 auto;
`;

const StyledSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const CampaignAnalyticsLayoutRoot = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const PAGE_SIZE = 10;
const PAGE_NUMBER = 1;

const getPageCount = (campaignsList: CampaignListItem[], pageSize: number) =>
  Math.ceil(campaignsList.length / pageSize);

const getPaginatedCampaignsList = (pageNumber: number, pageSize: number, campaignsList: CampaignListItem[]) => {
  const startIndex = (pageNumber - 1) * pageSize;
  const endIndex = pageNumber * pageSize;
  return campaignsList.slice(startIndex, endIndex);
};

export const ListCampaignsPage = ({
  merchantId,
  venueIds = [],
  campaignTypesPermittedToEdit = [],
  timezone,
  country,
  merchantVenues = [],
}: ListCampaignsPageProps) => {
  const { currency, entityType } = useCampaignModuleContext();
  const isMerchantLevel = entityType === 'merchant';
  const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [sortBy, setSortBy] = React.useState<CampaignColumnIds>(CampaignColumnIds.START_DATE);
  const [reverseSort, setReverseSort] = React.useState(true);
  const isMobile = useIsMobile();
  const campaignsRoutes = useCampaignsRoutes();
  const history = useHistory();
  const intl = useIntl();
  const { campaignsList, isCampaignsLoading, isCampaignsError } = useCampaignsList({
    merchantId,
    venueIds: isMerchantLevel ? undefined : venueIds,
    timezone,
    country,
    isMerchantLevel,
    enabled: !(isMerchantLevel && merchantVenues.length === 0),
  });

  const isAdCampaignsEnabled = campaignTypesPermittedToEdit.includes(CampaignType.ADS);

  const { data: adCampaignsList = [], isFetching: isAdCampaignsLoading } = useQuery(
    CampaignsQueryKeys.GET_ALL_ADS(venueIds[0]),
    async () =>
      (await getAdCampaigns(venueIds[0])).results.map(campaign =>
        transformApiAdCampaignListItemToCampaignListItem(campaign, country, currency, venueIds[0], isMerchantLevel),
      ),
    { refetchOnWindowFocus: false, enabled: isAdCampaignsEnabled, retry: false },
  );

  const handleColumnSortChange = (columnId: CampaignColumnIds, direction: 'descend' | 'ascend') => {
    setSortBy(columnId);
    setReverseSort(direction === 'descend');

    telemetryInteractionClickCampaignTable(columnId);
  };

  const filteredCampaignsList = isMerchantLevel
    ? campaignsList.filter(
        campaign => ![CampaignType.ITEM_DISCOUNT, CampaignType.END_OF_DAY].includes(campaign.campaignType),
      )
    : campaignsList;
  const mergedCampaignsList = filteredCampaignsList.concat(adCampaignsList);
  // Do secondary sorting by start date and then by campaign status, displaying running campaigns on top when start dates are equal
  const sortRules = [sortBy, CampaignColumnIds.START_DATE, CampaignColumnIds.CAMPAIGN_STATUS];
  const sortOrders: ('asc' | 'desc')[] = [reverseSort ? 'desc' : 'asc', 'desc', 'desc'];
  const sortedCampaigns = orderBy(mergedCampaignsList, sortRules, sortOrders); // TODO: @adtech update sort of budget column to sort by spend after clarifying with PL

  const { configurationQuery, campaignTypesAvailableToEdit, campaignsNumberLimit } = useCampaignConfiguration({
    merchantId,
    venueIds,
    campaignTypesPermittedToEdit,
    enabled: !(isMerchantLevel && merchantVenues.length === 0),
  });
  const { isFetching: isActiveCampaignsCountLoading, activeCampaignsCount } = useActiveCampaignsCount({
    merchantId,
    venueIds: isMerchantLevel ? undefined : venueIds,
    enabled: !(isMerchantLevel && merchantVenues.length === 0),
  });
  const { isCampaignAnalyticsForListPageEnabled } = useIsCampaignAnalyticsForListPageEnabled();

  const [dateRange, setDateRange] = useState<DateRange>(() => transformPeriodToDateRange('last-28-days', timezone));
  const {
    isInitialLoading: isCampaignPerformanceInitialLoading,
    isFetching: isCampaignPerformanceFetching,
    campaignPerformance,
  } = useCampaignsPerformance({
    entityType,
    entityId: entityType === 'merchant' ? merchantId : venueIds[0],
    dateRange,
    enabled: isCampaignAnalyticsForListPageEnabled,
  });

  const isLoading =
    isCampaignsLoading || configurationQuery.isFetching || isActiveCampaignsCountLoading || isAdCampaignsLoading;

  const handleNewCampaignClick = () => {
    setIsNewCampaignModalOpen(true);
    telemetryInteractionClickNewCampaign();
    reportEvent('NEW_CAMPAIGN');
  };

  const handleClickListItem = (listItemKey: string) => {
    const [campaignId, campaignType] = listItemKey.split('_');
    if (campaignType === CampaignType.ADS) {
      history.push(`${campaignsRoutes.details(campaignId)}?campaignType=Ads`);
    } else {
      history.push(campaignsRoutes.details(campaignId));
    }
  };

  const handleNewCampaign = (campaignType: CampaignType) => {
    history.push(`${campaignsRoutes.new}?campaignType=${campaignType}`);
  };

  const {
    handleCloneCampaign,
    handleDeleteCampaign,
    handleEditCampaign,
    handleRestartCampaign,
    handleStopCampaign,
    handleTerminateCampaign,
  } = useCampaignHandlers({ merchantId, venueIds });

  useTelemetryCampaignsView();

  const { hasReachedCampaignsLimit } = useCampaignsLimitStatus({
    campaignsNumberLimit,
    activeCampaignsCount,
  });

  useEffect(() => {
    setPageNumber(1);
  }, [pageSize]);

  const ContentRootElement = isCampaignAnalyticsForListPageEnabled ? CampaignAnalyticsLayoutRoot : React.Fragment;

  const closeModalHandler = () => {
    setIsNewCampaignModalOpen(false);
    reportEvent('CHOOSE_CAMPAIGN_CANCEL');
  };

  return (
    <>
      <CampaignHeader title={intl.formatMessage({ id: 'campaigns.title' })}>
        {mergedCampaignsList.length > 0 && (
          <Button
            variant="blue"
            size="small"
            disabled={hasReachedCampaignsLimit && !isMerchantLevel}
            onClick={handleNewCampaignClick}
          >
            <FormattedMessage id="campaigns.new-campaign" />
          </Button>
        )}
      </CampaignHeader>
      {!isLoading && !isMerchantLevel && campaignsNumberLimit != null && activeCampaignsCount != null && (
        <CampaignLimitAlert campaignsNumberLimit={campaignsNumberLimit} activeCampaignsCount={activeCampaignsCount} />
      )}
      <StyledWoltModal isOpen={isNewCampaignModalOpen} isMobile={isMobile} onRequestClose={closeModalHandler}>
        <ModalHeader onCloseClick={closeModalHandler}>
          <Typography.Heading6>
            <FormattedMessage id="campaigns.choose-campaign" />
          </Typography.Heading6>
        </ModalHeader>

        <CampaignTypeSelector
          campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
          onNewCampaign={handleNewCampaign}
          onCancel={closeModalHandler}
        />
      </StyledWoltModal>

      {mergedCampaignsList.length === 0 && !isLoading && (
        <NewCampaignContainer>
          <CampaignTypeSelector
            campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
            onNewCampaign={handleNewCampaign}
          />
        </NewCampaignContainer>
      )}

      {isLoading ? (
        <StyledSpinnerWrapper>
          <Spinner size="large" />
        </StyledSpinnerWrapper>
      ) : (
        mergedCampaignsList.length > 0 &&
        (!isCampaignsError ? (
          <ContentRootElement>
            {isCampaignAnalyticsForListPageEnabled && (isCampaignPerformanceInitialLoading || campaignPerformance) && (
              <CampaignListMetrics
                campaignPerformance={campaignPerformance}
                isLoading={isCampaignPerformanceFetching}
                dateRange={dateRange}
                setDateRange={setDateRange}
              />
            )}
            <CampaignTable
              country={country}
              paginatedCampaignsList={getPaginatedCampaignsList(pageNumber, pageSize, sortedCampaigns)}
              pageNumber={pageNumber}
              pageSize={pageSize}
              totalPages={getPageCount(sortedCampaigns, pageSize)}
              campaignTypesAvailableToEdit={campaignTypesAvailableToEdit}
              setPageSize={setPageSize}
              setPageNumber={setPageNumber}
              onCampaignClick={handleClickListItem}
              onEditCampaign={handleEditCampaign}
              onCloneCampaign={handleCloneCampaign}
              onDeleteCampaign={handleDeleteCampaign}
              onTerminateCampaign={handleTerminateCampaign}
              onRestartCampaign={handleRestartCampaign}
              onStopCampaign={handleStopCampaign}
              handleColumnSortChange={handleColumnSortChange}
              sortBy={sortBy}
              reverseSort={reverseSort}
              merchantVenues={merchantVenues}
            />
          </ContentRootElement>
        ) : (
          <CampaignError />
        ))
      )}
    </>
  );
};
